import { cn, useClientTranslation } from '@/lib/core';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

export interface ProfileMembershipProps {
  autoRenewal?: boolean;
  onAutoRenewalChange?: (val: boolean) => void;
  /** When true, the renewal switch is shown as disabled and cannot be toggled. */
  renewalLocked?: boolean;
  /** Called instead of `onAutoRenewalChange` when the switch is tapped while `renewalLocked`. */
  onRenewalLockedAttempt?: () => void;
  onRequestUpgrade?: () => void;
  requestingUpgrade?: boolean;
  renewalSectionTitle?: string;
  renewalToggleLabel?: string;
  renewalToggleDescription?: string;
  upgradeSectionTitle?: string;
  upgradeDescription?: string;
  upgradeButtonLabel?: string;
}

export function ProfileMembership({
  autoRenewal = false,
  onAutoRenewalChange,
  renewalLocked = false,
  onRenewalLockedAttempt,
  onRequestUpgrade,
  requestingUpgrade = false,
  renewalSectionTitle,
  renewalToggleLabel,
  renewalToggleDescription,
  upgradeSectionTitle,
  upgradeDescription,
  upgradeButtonLabel,
}: ProfileMembershipProps) {
  const t = useClientTranslation();

  return (
    <div className="flex flex-col gap-6 px-4 pt-6 pb-12">
      <div className="flex flex-col gap-2">
        <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] leading-normal font-semibold">
          {renewalSectionTitle ?? t('App.Labels.MembershipRenewalTitle')}
        </h2>
        <div className="border-jncquoi-neutral-200 border-b-[0.5px] py-4">
          <Switch
            checked={autoRenewal}
            onCheckedChange={val => {
              if (renewalLocked) {
                onRenewalLockedAttempt?.();
                return;
              }
              onAutoRenewalChange?.(val);
            }}
            aria-disabled={renewalLocked}
            label={renewalToggleLabel ?? t('App.Labels.MembershipRenewalToggleLabel')}
            description={renewalToggleDescription ?? t('App.Labels.MembershipRenewalToggleDescription')}
            className={cn(renewalLocked && 'bg-jncquoi-neutral-100! cursor-not-allowed')}
            labelClassName="font-pp-neue-montreal text-jncquoi-neutral-1000"
          />
        </div>
      </div>

      <div className="flex flex-col gap-6 pb-4">
        <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[20px] leading-normal font-semibold">
          {upgradeSectionTitle ?? t('App.Labels.MembershipUpgradeTitle')}
        </h2>
        <div className="flex flex-col gap-6">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal tracking-[0.5px]">
            {upgradeDescription ?? t('App.Labels.MembershipUpgradeDescription')}
          </p>
          <Button
            variant="primary-solid"
            palette="dark"
            size="md"
            loading={requestingUpgrade}
            disabled={requestingUpgrade}
            onClick={onRequestUpgrade}
            className="w-full"
          >
            {upgradeButtonLabel ?? t('App.Labels.MembershipRequestUpgrade')}
          </Button>
        </div>
      </div>
    </div>
  );
}
